import { type PointerEvent as ReactPointerEvent } from 'react';
import { useMotionValue, useReducedMotion, useSpring, useTransform } from 'motion/react';

/**
 * Mouse-parallax 3D tilt. Returns spring-smoothed rotateX/rotateY motion values
 * plus pointer handlers. No-ops under prefers-reduced-motion / touch.
 */
export function useTilt(maxDeg = 9) {
  const reduce = useReducedMotion();
  const px = useMotionValue(0.5);
  const py = useMotionValue(0.5);
  const spring = { stiffness: 150, damping: 18, mass: 0.4 };
  const sx = useSpring(px, spring);
  const sy = useSpring(py, spring);

  const rotateY = useTransform(sx, [0, 1], [-maxDeg, maxDeg]);
  const rotateX = useTransform(sy, [0, 1], [maxDeg, -maxDeg]);

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (reduce || e.pointerType === 'touch') return;
    const rect = e.currentTarget.getBoundingClientRect();
    px.set((e.clientX - rect.left) / rect.width);
    py.set((e.clientY - rect.top) / rect.height);
  };
  const onPointerLeave = () => {
    px.set(0.5);
    py.set(0.5);
  };

  return { rotateX, rotateY, onPointerMove, onPointerLeave, enabled: !reduce };
}
